import { useCallback, useRef, useState } from 'react';

import { useAuth } from '../providers/auth-provider';

export type FileUploadStatus = 'idle' | 'uploading' | 'success' | 'error';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export function useFileUpload(conversationId: string) {
  const { token } = useAuth();
  const [status, setStatus] = useState<FileUploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const uploadFile = useCallback(
    (file: File): Promise<unknown> => {
      if (file.size > MAX_FILE_SIZE) {
        setError('File exceeds maximum allowed size of 25MB.');
        setStatus('error');
        return Promise.resolve(undefined);
      }

      setStatus('uploading');
      setUploadProgress(0);
      setError(null);

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(100);
            setStatus('success');
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              resolve(undefined);
            }
          } else {
            try {
              const body = JSON.parse(xhr.responseText) as { detail?: string };
              setError(body.detail ?? 'Upload failed');
            } catch {
              setError('Upload failed');
            }
            setStatus('error');
            resolve(undefined);
          }
        };

        xhr.onerror = () => {
          setError('Upload failed');
          setStatus('error');
          resolve(undefined);
        };

        xhr.onabort = () => {
          setStatus('idle');
          setUploadProgress(0);
          resolve(undefined);
        };

        const formData = new FormData();
        formData.append('file', file);

        xhr.open('POST', `/api/conversations/${conversationId}/files`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });
    },
    [conversationId, token],
  );

  const cancelUpload = useCallback(() => {
    xhrRef.current?.abort();
  }, []);

  return { uploadFile, cancelUpload, status, uploadProgress, error };
}
